import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Archive,
  ArchiveRestore,
  Copy,
  ExternalLink,
  FileText,
  Image as ImageIcon,
  Link2,
  Loader2,
  Lock,
  MoreVertical,
  Pencil,
  Plus,
  Search,
  Star,
  Trash2,
  X,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { AdminModal, BulkDeleteBar, Field, Pill, SelectCheckbox, inputCls, textareaCls, useBulkDelete } from "@/pages/admin/ui";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent } from "@/components/ui/dialog";

const imageCache = new Map();

function usePrivateImage(path) {
  const [src, setSrc] = useState(() => (typeof imageCache.get(path) === "string" ? imageCache.get(path) : ""));
  useEffect(() => {
    if (!path) {
      setSrc("");
      return undefined;
    }
    let active = true;
    const cached = imageCache.get(path);
    if (typeof cached === "string") {
      setSrc(cached);
      return undefined;
    }
    const promise =
      cached ||
      api
        .get(`/admin/private-notes/files/${path}`, { responseType: "blob" })
        .then(({ data }) => {
          const url = URL.createObjectURL(data);
          imageCache.set(path, url);
          return url;
        })
        .catch(() => {
          imageCache.delete(path);
          return "";
        });
    imageCache.set(path, promise);
    promise.then((url) => active && setSrc(url));
    return () => {
      active = false;
    };
  }, [path]);
  return src;
}

function PrivateImage({ path, alt = "", className = "", onClick }) {
  const src = usePrivateImage(path);
  if (!src) {
    return (
      <div className={`flex items-center justify-center bg-slate-100 ${className}`}>
        <ImageIcon className="h-5 w-5 text-slate-300" />
      </div>
    );
  }
  return <img src={src} alt={alt} className={className} onClick={onClick} />;
}

const TYPE_META = {
  lien: { label: "Lien", tone: "blue", icon: Link2 },
  note: { label: "Note", tone: "amber", icon: FileText },
  image: { label: "Image", tone: "green", icon: ImageIcon },
};

const FILTERS = [
  { key: "all", label: "Toutes", icon: Lock },
  { key: "links", label: "Liens", icon: Link2 },
  { key: "notes", label: "Notes", icon: FileText },
  { key: "images", label: "Images", icon: ImageIcon },
  { key: "important", label: "Importantes", icon: Star },
  { key: "archived", label: "Archives", icon: Archive },
];

const SORTS = [
  { value: "recent", label: "Plus récentes" },
  { value: "oldest", label: "Plus anciennes" },
  { value: "az", label: "A → Z" },
  { value: "za", label: "Z → A" },
];

const DEFAULT_CATEGORIES = ["Travail", "Site web", "Fournisseurs", "Paiement", "Réseaux sociaux", "Idées", "Informatique", "Personnel", "Autre"];

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }) : "";
const fmtDateTime = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : "";

const EMPTY_FORM = { title: "", category: "", content: "", link: "", image_path: "", tags: "", is_important: false };

function NoteImageField({ value, onChange }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const body = new FormData();
    body.append("file", file);
    try {
      const { data } = await api.post("/admin/private-notes/upload", body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.path);
      toast.success("Image téléversée");
    } catch (error) {
      toast.error(apiError(error, "Upload impossible"));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div>
      <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-wide text-slate-500">
        Image (facultatif)
      </span>
      <div className="flex items-start gap-3">
        <div className="relative flex h-24 w-32 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-line bg-slate-50">
          {value ? (
            <>
              <PrivateImage path={value} className="h-full w-full object-cover" />
              <button
                type="button"
                data-testid="note-form-image-clear"
                onClick={() => onChange("")}
                className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-white/90 text-slate-500 shadow"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </>
          ) : (
            <ImageIcon className="h-6 w-6 text-slate-300" />
          )}
        </div>
        <div className="flex-1 space-y-1.5">
          <input
            ref={inputRef}
            type="file"
            accept=".jpg,.jpeg,.png,.webp"
            data-testid="note-form-image-input"
            onChange={(e) => upload(e.target.files?.[0])}
            className="hidden"
          />
          <button
            type="button"
            data-testid="note-form-image-btn"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-2 rounded-lg border border-line px-3 py-2 text-xs font-bold text-ink transition-colors duration-200 hover:border-brand hover:text-brand disabled:opacity-60"
          >
            {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImageIcon className="h-3.5 w-3.5" />}
            {uploading ? "Téléversement..." : value ? "Remplacer l'image" : "Ajouter une image"}
          </button>
          <p className="text-[11px] text-slate-400">PNG, JPG, JPEG, WEBP (max. 5 Mo)</p>
        </div>
      </div>
    </div>
  );
}

function NoteFormModal({ open, note, categories, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(note?.id);

  useEffect(() => {
    if (open) {
      setForm(
        note?.id
          ? {
              title: note.title || "",
              category: note.category || "",
              content: note.content || "",
              link: note.link || "",
              image_path: note.image_path || "",
              tags: (note.tags || []).join(", "),
              is_important: Boolean(note.is_important),
            }
          : EMPTY_FORM,
      );
    }
  }, [open, note]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e?.target ? e.target.value : e }));

  const save = async () => {
    if (!form.title.trim()) {
      toast.error("Le titre est requis");
      return;
    }
    setSaving(true);
    const payload = {
      title: form.title.trim(),
      category: form.category.trim(),
      content: form.content,
      link: form.link.trim(),
      image_path: form.image_path,
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      is_important: form.is_important,
    };
    try {
      const { data } = isEdit
        ? await api.put(`/admin/private-notes/${note.id}`, payload)
        : await api.post("/admin/private-notes", payload);
      toast.success(isEdit ? "Modifications enregistrées" : "Note enregistrée avec succès");
      onSaved(data);
    } catch (error) {
      toast.error(apiError(error, "Enregistrement impossible"));
    } finally {
      setSaving(false);
    }
  };

  const catOptions = Array.from(new Set([...DEFAULT_CATEGORIES, ...(categories || [])]));

  return (
    <AdminModal open={open} onOpenChange={(v) => !v && onClose()} title={isEdit ? "Modifier la note" : "Nouvelle note"} wide>
      <div className="space-y-4" data-testid="note-form-modal">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Titre *">
            <input data-testid="note-form-title" value={form.title} onChange={set("title")} placeholder="Ex : Compte Canva Pro" className={inputCls} />
          </Field>
          <Field label="Catégorie" hint="Choisissez ou saisissez une nouvelle catégorie">
            <input
              data-testid="note-form-category"
              list="note-categories"
              value={form.category}
              onChange={set("category")}
              placeholder="Ex : Travail"
              className={inputCls}
            />
            <datalist id="note-categories">
              {catOptions.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
          </Field>
        </div>

        <Field label="Contenu / Note">
          <textarea
            data-testid="note-form-content"
            value={form.content}
            onChange={set("content")}
            rows={5}
            placeholder="Écrivez librement vos informations…"
            className={textareaCls}
          />
        </Field>

        <Field label="Lien (facultatif)">
          <div className="flex gap-2">
            <input data-testid="note-form-link" value={form.link} onChange={set("link")} placeholder="https://www.exemple.com" className={inputCls} />
            {form.link.trim() && (
              <a
                href={form.link.trim()}
                target="_blank"
                rel="noreferrer"
                data-testid="note-form-open-link"
                className="flex h-11 shrink-0 items-center gap-1.5 rounded-lg border border-line px-3 text-xs font-bold text-ink transition-colors duration-200 hover:border-brand hover:text-brand"
              >
                <ExternalLink className="h-3.5 w-3.5" /> Ouvrir le lien
              </a>
            )}
          </div>
        </Field>

        <NoteImageField value={form.image_path} onChange={(v) => setForm((f) => ({ ...f, image_path: v }))} />

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Tags" hint="Séparés par des virgules">
            <input data-testid="note-form-tags" value={form.tags} onChange={set("tags")} placeholder="design, accès, urgent" className={inputCls} />
          </Field>
          <label className="flex cursor-pointer items-center gap-2.5 self-end rounded-lg border border-line px-3 py-3">
            <input
              type="checkbox"
              data-testid="note-form-important"
              checked={form.is_important}
              onChange={(e) => setForm((f) => ({ ...f, is_important: e.target.checked }))}
              className="h-4 w-4 accent-amber-500"
            />
            <span className="flex items-center gap-1.5 text-sm font-semibold text-ink">
              <Star className="h-4 w-4 text-amber-500" /> Marquer comme important
            </span>
          </label>
        </div>

        <div className="flex justify-end gap-2 border-t border-line pt-4">
          <button
            type="button"
            data-testid="note-form-cancel"
            onClick={onClose}
            className="rounded-lg border border-line px-4 py-2.5 text-sm font-bold text-slate-600 transition-colors duration-200 hover:text-ink"
          >
            Annuler
          </button>
          <button
            type="button"
            data-testid="note-form-save"
            onClick={save}
            disabled={saving}
            className="flex items-center gap-2 rounded-lg bg-brand px-5 py-2.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-brand/90 disabled:opacity-60"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {isEdit ? "Enregistrer les modifications" : "Enregistrer la note"}
          </button>
        </div>
      </div>
    </AdminModal>
  );
}

function NoteCard({ note, selected, onSelect, onAction }) {
  const meta = TYPE_META[note.type] || TYPE_META.note;
  const Icon = meta.icon;
  return (
    <button
      type="button"
      data-testid={`note-card-${note.id}`}
      onClick={() => onSelect(note.id)}
      className={`w-full rounded-2xl border bg-white p-4 text-left transition-colors duration-200 ${
        selected ? "border-brand ring-2 ring-brand/15" : "border-line hover:border-brand/40"
      }`}
    >
      <div className="flex items-start gap-3">
        {note.image_path ? (
          <PrivateImage path={note.image_path} className="h-14 w-14 shrink-0 rounded-xl border border-line object-cover" />
        ) : (
          <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl border border-line bg-slate-50">
            <Icon className="h-5 w-5 text-slate-400" />
          </span>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="truncate text-sm font-bold text-ink">{note.title}</span>
            {note.is_important && <Star className="h-3.5 w-3.5 shrink-0 fill-amber-400 text-amber-400" />}
          </div>
          {note.link && (
            <a
              href={note.link}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="mt-0.5 flex items-center gap-1 truncate text-xs font-semibold text-brand hover:underline"
            >
              <Link2 className="h-3 w-3 shrink-0" />
              <span className="truncate">{note.link}</span>
            </a>
          )}
          {note.content && <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-slate-500">{note.content}</p>}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1.5">
          <Pill tone={meta.tone}>{meta.label}</Pill>
          <span className="text-[11px] text-slate-400">{fmtDate(note.created_at)}</span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <span
                role="button"
                tabIndex={0}
                data-testid={`note-card-menu-${note.id}`}
                onClick={(e) => e.stopPropagation()}
                className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 transition-colors duration-200 hover:bg-slate-100 hover:text-ink"
              >
                <MoreVertical className="h-4 w-4" />
              </span>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
              <DropdownMenuItem data-testid={`note-menu-open-${note.id}`} onClick={() => onSelect(note.id)}>
                Ouvrir
              </DropdownMenuItem>
              <DropdownMenuItem data-testid={`note-menu-edit-${note.id}`} onClick={() => onAction("edit", note)}>
                <Pencil className="mr-2 h-3.5 w-3.5" /> Modifier
              </DropdownMenuItem>
              <DropdownMenuItem data-testid={`note-menu-duplicate-${note.id}`} onClick={() => onAction("duplicate", note)}>
                <Copy className="mr-2 h-3.5 w-3.5" /> Dupliquer
              </DropdownMenuItem>
              <DropdownMenuItem data-testid={`note-menu-important-${note.id}`} onClick={() => onAction("important", note)}>
                <Star className="mr-2 h-3.5 w-3.5" /> {note.is_important ? "Retirer des importantes" : "Marquer comme important"}
              </DropdownMenuItem>
              <DropdownMenuItem data-testid={`note-menu-archive-${note.id}`} onClick={() => onAction(note.is_archived ? "restore" : "archive", note)}>
                {note.is_archived ? <ArchiveRestore className="mr-2 h-3.5 w-3.5" /> : <Archive className="mr-2 h-3.5 w-3.5" />}
                {note.is_archived ? "Restaurer" : "Archiver"}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                data-testid={`note-menu-delete-${note.id}`}
                onClick={() => onAction("delete", note)}
                className="text-rose-600 focus:text-rose-600"
              >
                <Trash2 className="mr-2 h-3.5 w-3.5" /> Supprimer
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </button>
  );
}

function NoteDetail({ note, onClose, onAction, onZoom }) {
  const meta = TYPE_META[note.type] || TYPE_META.note;
  const copyLink = () => {
    navigator.clipboard.writeText(note.link).then(() => toast.success("Lien copié"));
  };
  return (
    <div className="rounded-2xl border border-line bg-white p-5" data-testid="note-detail">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-3">
          {note.image_path ? (
            <PrivateImage
              path={note.image_path}
              className="h-16 w-16 shrink-0 cursor-zoom-in rounded-xl border border-line object-cover"
              onClick={() => onZoom(note.image_path)}
            />
          ) : (
            <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl border border-line bg-slate-50">
              <meta.icon className="h-6 w-6 text-slate-400" />
            </span>
          )}
          <div>
            <h3 className="font-display text-base font-bold text-ink" data-testid="note-detail-title">{note.title}</h3>
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              <Pill tone={meta.tone}>{meta.label}</Pill>
              {note.category && <Pill>{note.category}</Pill>}
              {note.is_important && <Pill tone="amber">★ Important</Pill>}
              {note.is_archived && <Pill tone="rose">Archivée</Pill>}
            </div>
          </div>
        </div>
        <button
          type="button"
          data-testid="note-detail-close"
          onClick={onClose}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-400 transition-colors duration-200 hover:bg-slate-100 hover:text-ink"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="mt-4 space-y-1 text-xs text-slate-500">
        <p>Créée le {fmtDateTime(note.created_at)}</p>
        <p>Modifiée le {fmtDateTime(note.updated_at)}</p>
      </div>

      {note.link && (
        <div className="mt-4">
          <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-500">Lien</p>
          <div className="flex items-center gap-2">
            <span className="min-w-0 flex-1 truncate rounded-lg border border-line bg-slate-50 px-3 py-2.5 text-xs font-semibold text-brand">
              {note.link}
            </span>
            <a
              href={note.link}
              target="_blank"
              rel="noreferrer"
              data-testid="note-detail-open-link"
              className="flex h-9 shrink-0 items-center gap-1.5 rounded-lg border border-line px-3 text-xs font-bold text-ink transition-colors duration-200 hover:border-brand hover:text-brand"
              title="Ouvrir le lien"
            >
              <ExternalLink className="h-3.5 w-3.5" /> Ouvrir
            </a>
            <button
              type="button"
              data-testid="note-detail-copy-link"
              onClick={copyLink}
              className="flex h-9 shrink-0 items-center gap-1.5 rounded-lg border border-line px-3 text-xs font-bold text-ink transition-colors duration-200 hover:border-brand hover:text-brand"
              title="Copier le lien"
            >
              <Copy className="h-3.5 w-3.5" /> Copier
            </button>
          </div>
        </div>
      )}

      {note.content && (
        <div className="mt-4">
          <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-500">Note</p>
          <div className="whitespace-pre-wrap rounded-xl border border-line bg-slate-50 p-3.5 text-sm leading-relaxed text-ink" data-testid="note-detail-content">
            {note.content}
          </div>
        </div>
      )}

      {note.image_path && (
        <div className="mt-4">
          <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-500">Image</p>
          <PrivateImage
            path={note.image_path}
            className="max-h-64 w-full cursor-zoom-in rounded-xl border border-line object-contain"
            onClick={() => onZoom(note.image_path)}
          />
          <p className="mt-1 text-[11px] text-slate-400">Cliquez sur l'image pour l'agrandir</p>
        </div>
      )}

      {(note.tags || []).length > 0 && (
        <div className="mt-4">
          <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-500">Tags</p>
          <div className="flex flex-wrap gap-1.5">
            {note.tags.map((tag) => (
              <span key={tag} className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-600">
                #{tag}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="mt-5 border-t border-line pt-4">
        <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-slate-500">Actions</p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            data-testid="note-detail-edit"
            onClick={() => onAction("edit", note)}
            className="flex items-center gap-1.5 rounded-lg bg-brand px-4 py-2.5 text-xs font-bold text-white transition-colors duration-200 hover:bg-brand/90"
          >
            <Pencil className="h-3.5 w-3.5" /> Modifier
          </button>
          <button
            type="button"
            data-testid="note-detail-delete"
            onClick={() => onAction("delete", note)}
            className="flex items-center gap-1.5 rounded-lg border border-rose-200 px-4 py-2.5 text-xs font-bold text-rose-600 transition-colors duration-200 hover:bg-rose-50"
          >
            <Trash2 className="h-3.5 w-3.5" /> Supprimer
          </button>
          <button
            type="button"
            data-testid="note-detail-archive"
            onClick={() => onAction(note.is_archived ? "restore" : "archive", note)}
            className="flex items-center gap-1.5 rounded-lg border border-line px-4 py-2.5 text-xs font-bold text-ink transition-colors duration-200 hover:border-brand hover:text-brand"
          >
            {note.is_archived ? <ArchiveRestore className="h-3.5 w-3.5" /> : <Archive className="h-3.5 w-3.5" />}
            {note.is_archived ? "Restaurer" : "Archiver"}
          </button>
        </div>
      </div>

      <p className="mt-4 flex items-center gap-2 rounded-xl bg-brand-light px-3.5 py-2.5 text-[11px] font-semibold text-brand">
        <Lock className="h-3.5 w-3.5 shrink-0" /> Cette note est privée et visible uniquement par vous.
      </p>
    </div>
  );
}

export default function AdminPrivateNotes() {
  const [data, setData] = useState({ items: [], counts: {}, categories: [] });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("recent");
  const [selectedId, setSelectedId] = useState(null);
  const [modal, setModal] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [zoomPath, setZoomPath] = useState("");
  const debounce = useRef(null);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    try {
      const { data: res } = await api.get("/admin/private-notes", { params: { q, filter, sort } });
      setData(res);
    } catch (error) {
      toast.error(apiError(error, "Impossible de charger vos notes"));
    } finally {
      setLoading(false);
    }
  }, [q, filter, sort]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    debounce.current && clearTimeout(debounce.current);
    debounce.current = setTimeout(() => setQ(search), 300);
    return () => clearTimeout(debounce.current);
  }, [search]);

  const notes = data.items || [];
  const counts = data.counts || {};
  const selected = notes.find((n) => n.id === selectedId) || null;
  const bulk = useBulkDelete("/admin/private-notes/bulk-delete", load, "note(s)");

  const runAction = async (action, note) => {
    if (action === "edit") {
      setModal({ note });
      return;
    }
    if (action === "delete") {
      setDeleteTarget(note);
      return;
    }
    try {
      if (action === "duplicate") {
        const { data: copy } = await api.post(`/admin/private-notes/${note.id}/duplicate`);
        toast.success("Note dupliquée");
        setSelectedId(copy.id);
      } else if (action === "important") {
        await api.post(`/admin/private-notes/${note.id}/important`);
      } else if (action === "archive") {
        await api.post(`/admin/private-notes/${note.id}/archive`);
        toast.success("Note archivée");
        if (selectedId === note.id) setSelectedId(null);
      } else if (action === "restore") {
        await api.post(`/admin/private-notes/${note.id}/restore`);
        toast.success("Note restaurée");
      }
      load();
    } catch (error) {
      toast.error(apiError(error, "Action impossible"));
    }
  };

  const confirmDelete = async () => {
    const note = deleteTarget;
    setDeleteTarget(null);
    try {
      await api.delete(`/admin/private-notes/${note.id}`);
      toast.success("Note supprimée");
      if (selectedId === note.id) setSelectedId(null);
      load();
    } catch (error) {
      toast.error(apiError(error, "Suppression impossible"));
    }
  };

  return (
    <div data-testid="private-notes-page">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2.5 font-display text-2xl font-extrabold text-ink">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-ink text-white">
              <Lock className="h-5 w-5" />
            </span>
            Mes notes privées
          </h1>
          <p className="mt-1.5 text-sm text-slate-500">Enregistrez vos informations importantes, liens, images et notes.</p>
          <p className="text-xs font-semibold text-brand">Ces informations sont visibles uniquement par vous.</p>
        </div>
        <button
          type="button"
          data-testid="private-notes-new-btn"
          onClick={() => setModal({ note: null })}
          className="flex items-center gap-2 rounded-xl bg-brand px-5 py-3 text-sm font-bold text-white transition-colors duration-200 hover:bg-brand/90"
        >
          <Plus className="h-4 w-4" /> Nouvelle note
        </button>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative min-w-[220px] flex-1 sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="private-notes-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher une note…"
            className={`${inputCls} pl-9`}
          />
        </div>
        <select
          data-testid="private-notes-sort"
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className="h-11 rounded-lg border border-line bg-white px-3 text-sm font-semibold text-ink focus:border-brand focus:outline-none"
        >
          {SORTS.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
      </div>

      <div className="mb-5 flex gap-2 overflow-x-auto no-scrollbar">
        {FILTERS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            type="button"
            data-testid={`private-notes-filter-${key}`}
            onClick={() => {
              setFilter(key);
              setSelectedId(null);
            }}
            className={`flex shrink-0 items-center gap-1.5 rounded-full px-3.5 py-2 text-xs font-bold transition-colors duration-200 ${
              filter === key ? "bg-ink text-white" : "border border-line bg-white text-slate-600 hover:border-brand hover:text-brand"
            }`}
          >
            <Icon className="h-3.5 w-3.5" />
            {label}
            {counts[key] !== undefined && <span className="opacity-70">({counts[key]})</span>}
          </button>
        ))}
      </div>

      <div className="flex items-start gap-5">
        <div className="min-w-0 flex-1 space-y-3">
          {loading ? (
            <div className="flex justify-center rounded-2xl border border-line bg-white py-16">
              <Loader2 className="h-6 w-6 animate-spin text-brand" />
            </div>
          ) : notes.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-line bg-white py-16 text-center" data-testid="private-notes-empty">
              <Lock className="mx-auto h-8 w-8 text-slate-300" />
              <p className="mt-3 text-sm font-semibold text-slate-500">Aucune note trouvée</p>
              <p className="mt-1 text-xs text-slate-400">Créez votre première note avec le bouton « Nouvelle note ».</p>
            </div>
          ) : (
            <>
              <div className="mb-2 flex items-center gap-2 rounded-xl border border-line bg-white px-4 py-2.5 text-xs font-semibold text-slate-500">
                <SelectCheckbox testId="bulk-all-notes"
                  checked={notes.length > 0 && bulk.selected.length >= notes.length}
                  onChange={() => bulk.toggleAll(notes.map((n) => n.id))} />
                Tout sélectionner
              </div>
              <BulkDeleteBar count={bulk.selected.length} onDelete={bulk.bulkDelete} testId="bulk-bar-notes" />
              {notes.map((note) => (
                <div key={note.id} className="relative">
                  <span className="absolute right-3 top-3 z-10 rounded-md bg-white/90 p-0.5 shadow-sm">
                    <SelectCheckbox testId={`bulk-note-${note.id}`}
                      checked={bulk.selected.includes(note.id)} onChange={() => bulk.toggle(note.id)} />
                  </span>
                  <NoteCard note={note} selected={note.id === selectedId} onSelect={setSelectedId} onAction={runAction} />
                </div>
              ))}
              <p className="pt-1 text-center text-xs text-slate-400" data-testid="private-notes-total">
                {notes.length} note{notes.length > 1 ? "s" : ""} affichée{notes.length > 1 ? "s" : ""}
              </p>
            </>
          )}
        </div>

        {selected && (
          <aside className="hidden w-[400px] shrink-0 lg:block">
            <div className="sticky top-[88px]">
              <NoteDetail note={selected} onClose={() => setSelectedId(null)} onAction={runAction} onZoom={setZoomPath} />
            </div>
          </aside>
        )}
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-surface p-4 lg:hidden">
          <NoteDetail note={selected} onClose={() => setSelectedId(null)} onAction={runAction} onZoom={setZoomPath} />
        </div>
      )}

      <NoteFormModal
        open={Boolean(modal)}
        note={modal?.note}
        categories={data.categories}
        onClose={() => setModal(null)}
        onSaved={(saved) => {
          setModal(null);
          setSelectedId(saved.id);
          load();
        }}
      />

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="note-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Êtes-vous sûr de vouloir supprimer cette note ?</AlertDialogTitle>
            <AlertDialogDescription>
              « {deleteTarget?.title} » sera définitivement supprimée. Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="note-delete-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction
              data-testid="note-delete-confirm"
              onClick={confirmDelete}
              className="bg-rose-600 text-white hover:bg-rose-700"
            >
              Supprimer définitivement
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={Boolean(zoomPath)} onOpenChange={(v) => !v && setZoomPath("")}>
        <DialogContent aria-describedby={undefined} className="max-w-4xl p-2" data-testid="note-image-lightbox">
          {zoomPath && <PrivateImage path={zoomPath} className="max-h-[80vh] w-full rounded-lg object-contain" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
